/* eslint-disable yoda, max-lines */
import {
    Address,
    checkUniqueAddress,
    deleteCaptchaForAddress,
    TivanoData,
    getProducts,
    TivanoProductDetails,
    JarvisProduct,
    getJarvisProductsData,
    JarvisRequestData,
    getProductDetails,
    getAddressCookie,
    BIDResponse,
    setBID,
    getJarvisSuggestionData,
    JarvisData,
    AvailabilityIndicator,
} from '@vfde-sails/avc-core';
import { select, put, call } from 'redux-saga/effects';
import { resetCaptcha, getCaptchaToken, listenForCaptchaVerify } from '../../../components/captcha';
import { isApiError } from '../util/typeGuard';
import { setAddresses, setLoading } from '../action';
import { AddressForm } from '../interface';
import {
    selectForm,
    selectAddresses,
    selectAccessSupplier,
    selectAvailabilityIndicator,
} from '../selector';
import {
    openOverlay,
    setActiveOverlay,
    setLoading as setLpLoading,
    setPreMarketing,
    setTariffs,
} from '../../App/action';
import { OverlayContent } from '../../../enums/elementId';
import { Cookies, HttpStatus } from '../../../enums/network';
import { trackCaptcha } from '../../../util/tracking';
import {
    selectProviderType,
    selectSelected,
    selectTariffs,
} from '../../App/selector';
import { updateRegioChargeLinks } from './jarvisHelper';
import { ErrorMessage } from '../enum';
import { filterOnlyRelevantTariffs } from './zpApiHelper';
import { MetacodePrefix, PreMarketingType, ProviderType } from '../../../enums/product';
import { getCMSConfig, getTariffIds } from '../../../util/cms';
import { Selected } from '../../App/interface';
import { deleteCookie, setCookie } from '@vfde-sails/utils';
import { PRE_FIBER_BID, DGW, OXGG } from '../../../constants';
import { DTAG, GRUEN, VFDE } from '../constants';

export function* addressSaga () {
    const { city, street, number, index }: AddressForm = yield select(selectForm);
    const nonUniqueAddresses: Address[] = yield select(selectAddresses);

    const address: Address | undefined = nonUniqueAddresses[index];

    // if an adress was selected by the user find it based on the index and return it
    if (address) {
        yield put(setAddresses([address]));
        return address;
    }

    // otherwise check the address with the service
    const addressList: Address[] = yield call(checkUniqueAddress, city, street, number);

    yield put(setAddresses(addressList));

    switch (addressList.length){
        case 1:
            return addressList[0];
        case 0:
            throw new Error(ErrorMessage.ADDRESS_NOT_FOUND);
        default:
            throw new Error(ErrorMessage.NON_UNIQUE_ADDRESS);
    }
}

export function* captchaSaga () {
    yield call(resetCaptcha);

    // if captcha autovalidates after reset and we have a token then return
    const token: string = yield call(getCaptchaToken);

    if (token) {
        return;
    }

    // otherwise wait for user to verify captcha
    yield put(openOverlay(OverlayContent.CAPTCHA));
    yield put(setLoading(false));
    yield put(setLpLoading(false));
    yield call(trackCaptcha);
    yield call(listenForCaptchaVerify);
    yield put(setActiveOverlay(OverlayContent.FORM));
    yield put(setLoading());
}

export function* getFastestAltTivanoTariffId (metacodes: string[]) {
    if (
        !metacodes.length
        || !metacodes.some(code => code.includes(MetacodePrefix.DSL))
        || metacodes.some(code => code.includes(MetacodePrefix.FIBER))
    ) {
        return undefined;
    }

    const tariffs: JarvisData = yield call(getJarvisSuggestionData, {
        salesChannel: 'DSL',
        selectedId: getCMSConfig()?.selected?.tariffId ?? '',
        productId: metacodes,
    });

    return tariffs.fastest?.id;
}

export function* getPreMarketingData (defaultBIdCodes: string[], address: Address, captcha: string) {
    const isBHS = defaultBIdCodes.includes(getCMSConfig()?.homesPassedIds?.markerBHS ?? '');

    if (isBHS) {
        yield put(setPreMarketing(PreMarketingType.BHS));
    }

    // TODO: add CMS validator to remove fallback const
    const preFiberBId: BIDResponse | null = yield call(
        setBID,
        getCMSConfig()?.preFiberBannerId ?? PRE_FIBER_BID,
    );
    const preFiberIds: string[] = yield call(getProducts, address, captcha, preFiberBId?.vonr);

    // Reset b_id to avoid stuck pre fiber checks
    yield call(deleteCookie, Cookies.OSHOP);

    const isBKV = preFiberIds.includes(getCMSConfig()?.homesPassedIds?.markerBKV ?? '');

    if (isBHS && !isBKV) {
        return {
            metacodes: defaultBIdCodes,
            products: {},
        };
    }

    if (!preFiberIds.some(code => code.includes(MetacodePrefix.PRE_FIBER))) {
        return;
    }

    yield put(setPreMarketing(PreMarketingType.PRE_FIBER));

    // TODO: add CMS validator
    if (isBKV) {
        yield put(setPreMarketing(PreMarketingType.BKV));
    }
    defaultBIdCodes = defaultBIdCodes.filter(code => !code.includes(MetacodePrefix.FIBER));
    return {
        metacodes: [
            ...preFiberIds,
            // Note: Include for alternative suggestion
            ...defaultBIdCodes,
        ],
        products: {},
    };
}
/**
 * hasWholebuyLC
 * this function check if i have wholebuy product in the connected state
 * @param products
 */
export function hasWholebuyLC(products: TivanoProductDetails) {
    return Object.values(products).some(product => {
        const isFiber = product.accessSupplier === DTAG
        || product.accessSupplier === DGW ||
        product.accessSupplier === VFDE;
        const isOxg = product.accessSupplier === OXGG;
        return (isFiber &&
        (product.availabilityIndicator === GRUEN)) || isOxg;

    });
}
export function* getTivanoData (address: Address, captcha: string) {
    yield call(deleteCookie, Cookies.OSHOP);

    const defaultBId: BIDResponse | null = yield call(setBID, '');
    const tivanoMetacodes: string[] = yield call(getProducts, address, captcha, defaultBId?.vonr);

    // Note: Get only the fastest alternative product ID
    const fastestAlternativeId: string | undefined =
        yield call(getFastestAltTivanoTariffId, tivanoMetacodes);

    // Note: Workaround for ZP-API captcha limit
    const filteredMetacodes: string[] =
        yield call(filterOnlyRelevantTariffs, tivanoMetacodes, fastestAlternativeId);

    const tivanoProducts: TivanoProductDetails = filteredMetacodes.length
        && (yield call(getProductDetails, address, filteredMetacodes, defaultBId?.vonr));

    // Directly use the result of hasWholebuyLC in the if condition
    // if the wholybuy product is not in the green phase we need to check prefiber meda codes
    if (!hasWholebuyLC(tivanoProducts)) {
    // Fetch pre-marketing products
        const preMarketingProducts: TivanoData | undefined = yield call(
            getPreMarketingData,
            tivanoMetacodes,
            address,
            captcha
        );

        // Return pre-marketing products if they exist
        if (preMarketingProducts) {
            return preMarketingProducts;
        }
    }

    return {
        metacodes: filteredMetacodes,
        products: tivanoProducts ?? {},
    };
}

export function* tivanoSaga (address: Address, isSeamless = false) {
    try {


        const previousAddress: Address | undefined = yield call(getAddressCookie);

        if (!isSeamless && JSON.stringify(address) !== JSON.stringify(previousAddress)) {
            yield call(deleteCaptchaForAddress);
        }

        const result: TivanoData = yield call(getTivanoData, address, getCaptchaToken());

        return result;
    } catch (error) {
        if (!isApiError(error) || error.status !== HttpStatus.UNAUTHORIZED) {
            throw error;
        }

        yield call(captchaSaga);
    }

    const result: TivanoData = yield call(getTivanoData, address, getCaptchaToken());

    return result;
}

export function* updateOffersAfterVpSaga (tivano: TivanoProductDetails) {
    const selectedTariff: Selected = yield select(selectSelected);
    const activeProviderType: ProviderType = yield select(selectProviderType);
    const accessSupplier: string = yield select(selectAccessSupplier);
    const availabilityIndicator: AvailabilityIndicator = yield select(selectAvailabilityIndicator);
    const tariffIds: string[] = yield call(getTariffIds, activeProviderType);
    const request: JarvisRequestData = {
        salesChannel: 'FIB',
        selectedId: selectedTariff.tariffId,
        productId: tariffIds,
        tivano,
        ...(accessSupplier && { accessSupplier }),
        ...(availabilityIndicator && { availabilityIndicator }),
    };

    const products: JarvisProduct[] = yield call(getJarvisProductsData, request);
    const currentTariffs: JarvisProduct[] = yield select(selectTariffs);
    const updatedTariffs: JarvisProduct[] = yield call(updateRegioChargeLinks, currentTariffs, products);
    const tariffsToBeUpdated = [DGW, OXGG, VFDE];
    if (tariffsToBeUpdated.includes(accessSupplier)) {
        for (let tarrif = 0; tarrif < updatedTariffs.length; tarrif++) {
            let tarrifID: string = updatedTariffs[tarrif]?.id;
            if (tivano[tarrifID]) {
                let uploadSpeed = tivano[tarrifID]?.upstream?.toString()?.slice(0, -3);
                let downloadSpeed = tivano[tarrifID]?.downstream?.toString()?.slice(0, -3);
                updatedTariffs[tarrif].upload = uploadSpeed;
                updatedTariffs[tarrif].download = downloadSpeed;
            }
        }
    }
    yield put(setTariffs(updatedTariffs));
}
